import React, { FC, useCallback, useMemo } from "react";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import Typography from "@mui/material/Typography";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import { HeaderActionsIconButton } from "../Header/style";
import {
  SpeechRecognitionError,
  SpeechRecognitionStatus,
} from "../../voice/speech-recognition";

export interface VoiceButtonProps {
  status: SpeechRecognitionStatus;
  lastTranscript: string | null;
  lastError: SpeechRecognitionError | null;
  isSupported: boolean;
  onStart: () => void;
  onStop: () => void;
}

interface VoiceButtonConfig {
  icon: React.ReactElement;
  tooltip: string;
  color: string;
  isAnimating: boolean;
}

const ERROR_MESSAGES: Record<SpeechRecognitionError, string> = {
  [SpeechRecognitionError.NOT_SUPPORTED]:
    "Navegação por voz não suportada neste browser",
  [SpeechRecognitionError.PERMISSION_DENIED]: "Permissão de microfone negada",
  [SpeechRecognitionError.NO_SPEECH]: "Nenhuma fala detectada. Tente novamente",
  [SpeechRecognitionError.NETWORK]: "Erro de rede. Verifique sua conexão",
  [SpeechRecognitionError.UNKNOWN]: "Erro desconhecido. Tente novamente",
};

const IDLE_COLOR = "var(--color-link-contrast)";

export const VoiceButton: FC<VoiceButtonProps> = ({
  status,
  lastTranscript,
  lastError,
  isSupported,
  onStart,
  onStop,
}) => {
  const config = useMemo((): VoiceButtonConfig => {
    switch (status) {
      case SpeechRecognitionStatus.LISTENING:
        return {
          icon: <GraphicEqIcon />,
          tooltip: "Ouvindo... clique para parar",
          color: "#ff4444",
          isAnimating: true,
        };
      case SpeechRecognitionStatus.PROCESSING:
        return {
          icon: <MicIcon />,
          tooltip: "Processando comando...",
          color: "#ff891b",
          isAnimating: false,
        };
      case SpeechRecognitionStatus.ERROR:
        return {
          icon: <MicOffIcon />,
          tooltip: lastError
            ? ERROR_MESSAGES[lastError]
            : "Erro ao processar comando",
          color: "#ff4444",
          isAnimating: false,
        };
      default:
        return {
          icon: <MicIcon />,
          tooltip: "Clique para ativar navegação por voz",
          color: IDLE_COLOR,
          isAnimating: false,
        };
    }
  }, [status, lastError]);

  const handleClick = useCallback(() => {
    if (status === SpeechRecognitionStatus.LISTENING) {
      onStop();
      return;
    }
    onStart();
  }, [status, onStart, onStop]);

  if (!isSupported) {
    return null;
  }

  return (
    <Tooltip title={config.tooltip} arrow>
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <HeaderActionsIconButton
          onClick={handleClick}
          disabled={status === SpeechRecognitionStatus.PROCESSING}
          aria-label={config.tooltip}
          disableRipple
          sx={{
            color: config.color,
            position: "relative",
            transition: "color 0.3s ease",
          }}
        >
          {config.isAnimating && (
            <CircularProgress
              size={36}
              sx={{
                color: "#ff4444",
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: "-18px",
                marginLeft: "-18px",
                opacity: 0.6,
              }}
            />
          )}
          {config.icon}
        </HeaderActionsIconButton>

        <Fade
          in={!!lastTranscript && status === SpeechRecognitionStatus.PROCESSING}
        >
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              color: IDLE_COLOR,
              maxWidth: 160,
              textAlign: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: "10px",
            }}
          >
            {lastTranscript}
          </Typography>
        </Fade>
      </Box>
    </Tooltip>
  );
};
